import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Select,
    Button,
    Typography,
    Alert,
    Spin,
    Tag,
} from "antd";

import {
    DownloadOutlined,
    TeamOutlined,
    FilePdfOutlined,
    CalendarOutlined,
    UserOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    pdf,
} from "@react-pdf/renderer";

const {
    Text: AntText,
} = Typography;

const BLACK = "#142536";
const NAVY = "#062A4B";
const BLUE = "#063A6B";
const ORANGE = "#C0750D";
const GOLD = "#F6C944";
const PAGE_GRAY = "#F4F7F9";
const GRAY = "#6B7280";
const BORDER_GRAY = "#E0E7EC";
const SOFT_BLUE = "#EDF5FA";
const SOFT_ORANGE = "#FFF7ED";
const ORANGE_BORDER = "#F3D0A4";
const WHITE = "#FFFFFF";

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 30,
        paddingBottom: 35,
        paddingHorizontal: 35,
        backgroundColor: WHITE,
        fontFamily: "Helvetica",
    },

    header: {
        backgroundColor: NAVY,
        paddingVertical: 18,
        paddingHorizontal: 20,
        marginBottom: 18,
        borderBottomWidth: 4,
        borderBottomColor: GOLD,
    },

    schoolName: {
        color: WHITE,
        fontSize: 19,
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: 5,
    },

    schoolSubtitle: {
        color: "#D8E4EE",
        fontSize: 9,
        textAlign: "center",
    },

    titleContainer: {
        marginBottom: 15,
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: ORANGE_BORDER,
    },

    title: {
        color: BLACK,
        fontSize: 16,
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: 5,
    },

    className: {
        color: ORANGE,
        fontSize: 12,
        fontWeight: "bold",
        textAlign: "center",
    },

    academicYear: {
        color: GRAY,
        fontSize: 9,
        textAlign: "center",
        marginTop: 4,
    },

    table: {
        width: "100%",
        borderWidth: 1,
        borderColor: BORDER_GRAY,
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: NAVY,
        borderBottomWidth: 1,
        borderBottomColor: NAVY,
    },

    tableRow: {
        flexDirection: "row",
        minHeight: 30,
        borderBottomWidth: 1,
        borderBottomColor: BORDER_GRAY,
    },

    tableRowAlternate: {
        flexDirection: "row",
        minHeight: 30,
        backgroundColor: "#F7F9FB",
        borderBottomWidth: 1,
        borderBottomColor: BORDER_GRAY,
    },

    cell: {
        paddingVertical: 7,
        paddingHorizontal: 6,
        fontSize: 9,
        color: BLACK,
        justifyContent: "center",
    },

    headerCell: {
        paddingVertical: 8,
        paddingHorizontal: 6,
        fontSize: 9,
        fontWeight: "bold",
        color: WHITE,
        justifyContent: "center",
    },

    numberCell: {
        width: "8%",
        textAlign: "center",
    },

    nameCell: {
        width: "37%",
    },

    genderCell: {
        width: "15%",
        textAlign: "center",
    },

    classCell: {
        width: "20%",
        textAlign: "center",
    },

    statusCell: {
        width: "20%",
        textAlign: "center",
    },

    summaryContainer: {
        marginTop: 15,
        padding: 12,
        backgroundColor: "#F7F9FB",
        borderLeftWidth: 4,
        borderLeftColor: ORANGE,
    },

    summaryText: {
        color: BLACK,
        fontSize: 10,
        fontWeight: "bold",
    },

    footer: {
        position: "absolute",
        bottom: 15,
        left: 35,
        right: 35,
        borderTopWidth: 1,
        borderTopColor: ORANGE_BORDER,
        paddingTop: 6,
    },

    footerText: {
        color: GRAY,
        fontSize: 8,
        textAlign: "center",
    },
});

const StudentGradeClassPDF = ({
    students,
    gradeClassName,
    academicYearName,
}) => {
    const safeStudents = Array.isArray(students)
        ? students
        : [];

    return (
        <Document>
            <Page
                size="A4"
                orientation="portrait"
                style={pdfStyles.page}
            >
                <View style={pdfStyles.header}>
                    <Text style={pdfStyles.schoolName}>
                        Salvation In Christ School System
                    </Text>

                    <Text style={pdfStyles.schoolSubtitle}>
                        Students Admission Record
                    </Text>
                </View>

                <View style={pdfStyles.titleContainer}>
                    <Text style={pdfStyles.title}>
                        STUDENTS IN GRADE CLASS
                    </Text>

                    <Text style={pdfStyles.className}>
                        {gradeClassName || "N/A"}
                    </Text>

                    <Text style={pdfStyles.academicYear}>
                        Academic Year:{" "}
                        {academicYearName || "N/A"}
                    </Text>
                </View>

                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableHeader}>
                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.numberCell,
                            ]}
                        >
                            NO.
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.nameCell,
                            ]}
                        >
                            FULL NAME
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.genderCell,
                            ]}
                        >
                            GENDER
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.classCell,
                            ]}
                        >
                            GRADE CLASS
                        </Text>

                        <Text
                            style={[
                                pdfStyles.headerCell,
                                pdfStyles.statusCell,
                            ]}
                        >
                            STATUS
                        </Text>
                    </View>

                    {safeStudents.map(
                        (student, index) => (
                            <View
                                key={
                                    student?.id ||
                                    index
                                }
                                style={
                                    index % 2 === 0
                                        ? pdfStyles.tableRow
                                        : pdfStyles.tableRowAlternate
                                }
                                wrap={false}
                            >
                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.numberCell,
                                    ]}
                                >
                                    {index + 1}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.nameCell,
                                    ]}
                                >
                                    {student?.full_name ||
                                        "N/A"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.genderCell,
                                    ]}
                                >
                                    {student?.gender ||
                                        "N/A"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.classCell,
                                    ]}
                                >
                                    {student?.grade_class_name ||
                                        gradeClassName ||
                                        "N/A"}
                                </Text>

                                <Text
                                    style={[
                                        pdfStyles.cell,
                                        pdfStyles.statusCell,
                                    ]}
                                >
                                    {student?.status ||
                                        "N/A"}
                                </Text>
                            </View>
                        )
                    )}
                </View>

                <View style={pdfStyles.summaryContainer}>
                    <Text style={pdfStyles.summaryText}>
                        Total Students:{" "}
                        {safeStudents.length}
                    </Text>
                </View>

                <View style={pdfStyles.footer}>
                    <Text style={pdfStyles.footerText}>
                        Polu Integrated School Management System
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

const AdmissionStuListByClass = () => {
    const { selectedYear } = useAcademicYear();

    const [students, setStudents] = useState([]);
    const [
        selectedGradeClassId,
        setSelectedGradeClassId,
    ] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState("");

    const getToken = () => {
        return localStorage.getItem("access");
    };

    const normalizeStudentsResponse = (data) => {
        if (Array.isArray(data)) {
            return data;
        }

        if (Array.isArray(data?.results)) {
            return data.results;
        }

        if (Array.isArray(data?.students)) {
            return data.students;
        }

        if (Array.isArray(data?.data)) {
            return data.data;
        }

        return [];
    };

    const fetchStudents = async () => {
        const token = getToken();

        if (!token) {
            window.location.href = "/login";
            return;
        }

        if (!selectedYear?.id) {
            setStudents([]);
            return;
        }

        try {
            setLoading(true);
            setError("");

            const response = await api.get(
                `/api/users/students/?academic_year=${selectedYear.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const normalizedStudents =
                normalizeStudentsResponse(
                    response.data
                );

            setStudents(normalizedStudents);
            setSelectedGradeClassId(null);
        } catch (err) {
            console.error(
                "Error loading students:",
                err
            );

            if (err.response?.status === 401) {
                localStorage.removeItem("access");
                window.location.href = "/login";
                return;
            }

            setStudents([]);

            setError(
                err.response?.data?.detail ||
                    "Unable to load students."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSelectedGradeClassId(null);
        setStudents([]);

        if (selectedYear?.id) {
            fetchStudents();
        }
    }, [selectedYear]);

    const gradeClassOptions = useMemo(() => {
        const classesMap = new Map();

        const safeStudents = Array.isArray(students)
            ? students
            : [];

        safeStudents.forEach((student) => {
            if (
                student?.grade_class === null ||
                student?.grade_class === undefined
            ) {
                return;
            }

            const classId = student.grade_class;

            const className =
                student.grade_class_name ||
                `Grade Class ${classId}`;

            if (!classesMap.has(String(classId))) {
                classesMap.set(
                    String(classId),
                    {
                        value: classId,
                        label: className,
                        searchText:
                            String(
                                className
                            ).toLowerCase(),
                    }
                );
            }
        });

        return Array.from(
            classesMap.values()
        ).sort((a, b) =>
            a.label.localeCompare(
                b.label,
                undefined,
                {
                    numeric: true,
                    sensitivity: "base",
                }
            )
        );
    }, [students]);

    const selectedGradeClass = useMemo(() => {
        if (
            selectedGradeClassId === null ||
            selectedGradeClassId === undefined
        ) {
            return null;
        }

        const matchingStudent = students.find(
            (student) =>
                String(student.grade_class) ===
                String(selectedGradeClassId)
        );

        if (!matchingStudent) {
            return null;
        }

        return {
            id: matchingStudent.grade_class,
            name:
                matchingStudent.grade_class_name ||
                `Grade Class ${matchingStudent.grade_class}`,
        };
    }, [
        students,
        selectedGradeClassId,
    ]);

    const studentsInSelectedGrade = useMemo(() => {
        if (
            selectedGradeClassId === null ||
            selectedGradeClassId === undefined
        ) {
            return [];
        }

        return students.filter(
            (student) =>
                String(student?.grade_class) ===
                String(selectedGradeClassId)
        );
    }, [
        students,
        selectedGradeClassId,
    ]);

    const handleDownloadPDF = async () => {
        if (
            selectedGradeClassId === null ||
            selectedGradeClassId === undefined
        ) {
            setError(
                "Please select a Grade Class first."
            );
            return;
        }

        const safeStudents =
            Array.isArray(
                studentsInSelectedGrade
            )
                ? studentsInSelectedGrade
                : [];

        if (safeStudents.length === 0) {
            setError(
                "No students were found in the selected Grade Class."
            );
            return;
        }

        try {
            setDownloading(true);
            setError("");

            const gradeClassName =
                selectedGradeClass?.name ||
                "Grade Class";

            const blob = await pdf(
                <StudentGradeClassPDF
                    students={safeStudents}
                    gradeClassName={
                        gradeClassName
                    }
                    academicYearName={
                        selectedYear?.name
                    }
                />
            ).toBlob();

            const url =
                URL.createObjectURL(blob);

            const link =
                document.createElement("a");

            link.href = url;

            link.download =
                `${gradeClassName
                    .replace(
                        /[^a-z0-9]/gi,
                        "_"
                    )
                    .replace(
                        /_+/g,
                        "_"
                    )}_Students.pdf`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(
                "PDF generation error:",
                err
            );

            setError(
                "Unable to generate the PDF. Please try again."
            );
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="admission-grade-download">
            <div className="admission-grade-download-header">
                <div className="admission-grade-download-icon">
                    <FilePdfOutlined />
                </div>

                <div className="admission-grade-download-heading">
                    <AntText className="admission-grade-eyebrow">
                        Student Reports
                    </AntText>

                    <div className="admission-grade-title">
                        Download by Grade Class
                    </div>

                    <AntText className="admission-grade-description">
                        Generate a complete PDF
                        admission list for any grade
                        class in the selected academic
                        year.
                    </AntText>
                </div>

                <div className="admission-grade-year">
                    <CalendarOutlined />

                    <div>
                        <span>Academic Year</span>
                        <strong>
                            {selectedYear?.name ||
                                "Not selected"}
                        </strong>
                    </div>
                </div>
            </div>

            <div className="admission-grade-download-content">
                {error && (
                    <Alert
                        type="error"
                        showIcon
                        closable
                        message={error}
                        onClose={() =>
                            setError("")
                        }
                        className="admission-grade-alert"
                    />
                )}

                {loading ? (
                    <div className="admission-grade-loading">
                        <Spin />

                        <AntText>
                            Loading grade classes...
                        </AntText>
                    </div>
                ) : (
                    <>
                        <div className="admission-grade-selector">
                            <label>
                                Select Grade Class
                            </label>

                            <Select
                                showSearch
                                allowClear
                                size="large"
                                placeholder="Search and select a Grade Class"
                                prefix={
                                    <TeamOutlined
                                        style={{
                                            color: ORANGE,
                                        }}
                                    />
                                }
                                value={
                                    selectedGradeClassId
                                }
                                options={
                                    gradeClassOptions
                                }
                                style={{
                                    width: "100%",
                                }}
                                filterOption={(
                                    input,
                                    option
                                ) =>
                                    option?.searchText?.includes(
                                        input.toLowerCase()
                                    )
                                }
                                onChange={(value) => {
                                    setSelectedGradeClassId(
                                        value ??
                                            null
                                    );
                                    setError("");
                                }}
                                notFoundContent={
                                    <AntText
                                        style={{
                                            color: GRAY,
                                        }}
                                    >
                                        No Grade
                                        Classes found
                                    </AntText>
                                }
                            />
                        </div>

                        {students.length === 0 &&
                            !error && (
                                <Alert
                                    type="info"
                                    showIcon
                                    message="No students found"
                                    description={
                                        selectedYear?.id
                                            ? "There are no students available for the selected academic year."
                                            : "Please select an academic year."
                                    }
                                    className="admission-grade-alert"
                                />
                            )}

                        {selectedGradeClass && (
                            <div className="admission-selected-class">
                                <div className="admission-selected-class-main">
                                    <div className="admission-selected-class-icon">
                                        <TeamOutlined />
                                    </div>

                                    <div>
                                        <span>
                                            Selected Grade
                                            Class
                                        </span>

                                        <strong>
                                            {
                                                selectedGradeClass.name
                                            }
                                        </strong>
                                    </div>
                                </div>

                                <div className="admission-selected-year">
                                    <span>
                                        Academic Year
                                    </span>

                                    <strong>
                                        {selectedYear?.name ||
                                            "N/A"}
                                    </strong>
                                </div>

                                <Tag className="admission-student-count">
                                    <UserOutlined />

                                    <span>
                                        {
                                            studentsInSelectedGrade.length
                                        }{" "}
                                        Students
                                    </span>
                                </Tag>
                            </div>
                        )}

                        <div className="admission-download-action">
                            <Button
                                type="primary"
                                size="large"
                                icon={
                                    <DownloadOutlined />
                                }
                                loading={
                                    downloading
                                }
                                disabled={
                                    !selectedGradeClassId ||
                                    studentsInSelectedGrade.length ===
                                        0
                                }
                                onClick={
                                    handleDownloadPDF
                                }
                            >
                                {downloading
                                    ? "Generating PDF..."
                                    : "Download Grade Class PDF"}
                            </Button>

                            {selectedGradeClass &&
                                studentsInSelectedGrade.length >
                                    0 && (
                                    <div className="admission-download-ready">
                                        <CheckCircleOutlined />

                                        <span>
                                            {
                                                studentsInSelectedGrade.length
                                            }{" "}
                                            student records
                                            ready for
                                            download
                                        </span>
                                    </div>
                                )}
                        </div>
                    </>
                )}
            </div>

            <style>
                {`
                    .admission-grade-download {
                        overflow: hidden;
                        border-radius: 18px;
                        background: #FFFFFF;
                        border: 1px solid #E0E7EC;
                        box-shadow:
                            0 8px 28px rgba(6,42,75,.06);
                    }

                    .admission-grade-download-header {
                        min-height: 95px;
                        padding: 17px 20px;
                        display: flex;
                        align-items: center;
                        gap: 13px;
                        background:
                            linear-gradient(
                                120deg,
                                #F8FAFC 0%,
                                #FFFFFF 55%,
                                #FFF8EF 100%
                            );
                        border-bottom: 1px solid #E0E7EC;
                    }

                    .admission-grade-download-icon {
                        width: 46px;
                        height: 46px;
                        min-width: 46px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 12px;
                        background: #FFF3E7;
                        color: #C0750D;
                        border: 1px solid #F3D0A4;
                        font-size: 21px;
                    }

                    .admission-grade-download-heading {
                        min-width: 0;
                        flex: 1;
                    }

                    .admission-grade-eyebrow {
                        display: block;
                        color: #C0750D;
                        font-size: 9px;
                        line-height: 1.1;
                        font-weight: 800;
                        letter-spacing: 1px;
                        text-transform: uppercase;
                        margin-bottom: 4px;
                    }

                    .admission-grade-title {
                        color: #142536;
                        font-size: 18px;
                        line-height: 1.2;
                        font-weight: 800;
                    }

                    .admission-grade-description {
                        display: block;
                        margin-top: 4px;
                        color: #6B7280;
                        font-size: 11.5px;
                        line-height: 1.45;
                    }

                    .admission-grade-year {
                        min-width: 145px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        padding: 9px 11px;
                        border-radius: 9px;
                        background: #F4F7F9;
                        color: #C0750D;
                        border: 1px solid #E0E7EC;
                    }

                    .admission-grade-year > div {
                        min-width: 0;
                    }

                    .admission-grade-year span {
                        display: block;
                        color: #6B7280;
                        font-size: 8px;
                        line-height: 1.2;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: .6px;
                        margin-bottom: 2px;
                    }

                    .admission-grade-year strong {
                        display: block;
                        color: #142536;
                        font-size: 11.5px;
                    }

                    .admission-grade-download-content {
                        padding: 17px 20px 19px;
                    }

                    .admission-grade-alert {
                        margin-bottom: 13px;
                        border-radius: 9px;
                    }

                    .admission-grade-selector label {
                        display: block;
                        margin-bottom: 6px;
                        color: #142536;
                        font-size: 11px;
                        font-weight: 800;
                    }

                    .admission-grade-selector .ant-select-selector {
                        border-radius: 10px !important;
                        min-height: 43px !important;
                        border-color: #DCE4EA !important;
                        box-shadow: none !important;
                    }

                    .admission-grade-selector .ant-select-focused .ant-select-selector {
                        border-color: #C0750D !important;
                        box-shadow:
                            0 0 0 2px rgba(192,117,13,.08) !important;
                    }

                    .admission-selected-class {
                        margin-top: 13px;
                        padding: 11px 13px;
                        display: flex;
                        align-items: center;
                        gap: 15px;
                        flex-wrap: wrap;
                        border-radius: 11px;
                        background: #FFF8EF;
                        border: 1px solid #F3D0A4;
                    }

                    .admission-selected-class-main {
                        display: flex;
                        align-items: center;
                        gap: 9px;
                        flex: 1;
                        min-width: 190px;
                    }

                    .admission-selected-class-icon {
                        width: 34px;
                        height: 34px;
                        min-width: 34px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 9px;
                        background: #FFFFFF;
                        border: 1px solid #F3D0A4;
                        color: #C0750D;
                    }

                    .admission-selected-class-main span,
                    .admission-selected-year span {
                        display: block;
                        color: #6B7280;
                        font-size: 8.5px;
                        line-height: 1.2;
                        text-transform: uppercase;
                        letter-spacing: .6px;
                        margin-bottom: 3px;
                    }

                    .admission-selected-class-main strong {
                        display: block;
                        color: #142536;
                        font-size: 13px;
                    }

                    .admission-selected-year {
                        min-width: 125px;
                    }

                    .admission-selected-year strong {
                        color: #142536;
                        font-size: 12px;
                    }

                    .admission-student-count {
                        margin: 0;
                        height: 31px;
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        border: 0;
                        border-radius: 8px;
                        background: #FFFFFF;
                        color: #C0750D;
                        font-weight: 800;
                    }

                    .admission-download-action {
                        margin-top: 14px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .admission-download-action .ant-btn {
                        height: 42px;
                        padding: 0 17px;
                        border-radius: 9px;
                        background: #C0750D;
                        border-color: #C0750D;
                        font-weight: 750;
                        box-shadow:
                            0 5px 14px rgba(192,117,13,.16);
                    }

                    .admission-download-action .ant-btn:hover {
                        background: #A86208 !important;
                        border-color: #A86208 !important;
                    }

                    .admission-download-action .ant-btn[disabled] {
                        background: #D8DDE1 !important;
                        border-color: #D8DDE1 !important;
                        color: #8A9299 !important;
                        box-shadow: none;
                    }

                    .admission-download-ready {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        color: #4FAE22;
                        font-size: 10px;
                        font-weight: 700;
                    }

                    .admission-grade-loading {
                        min-height: 100px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 9px;
                        color: #6B7280;
                        font-size: 11px;
                    }

                    @media (max-width: 700px) {
                        .admission-grade-download-header {
                            align-items: flex-start;
                            flex-wrap: wrap;
                            padding: 15px;
                        }

                        .admission-grade-year {
                            width: 100%;
                            box-sizing: border-box;
                        }

                        .admission-grade-download-content {
                            padding: 14px 15px 16px;
                        }
                    }

                    @media (max-width: 500px) {
                        .admission-grade-download {
                            border-radius: 14px;
                        }

                        .admission-grade-download-header {
                            gap: 10px;
                        }

                        .admission-grade-download-icon {
                            width: 40px;
                            height: 40px;
                            min-width: 40px;
                            font-size: 18px;
                        }

                        .admission-grade-title {
                            font-size: 16px;
                        }

                        .admission-grade-description {
                            font-size: 10.5px;
                        }

                        .admission-selected-class {
                            align-items: flex-start;
                            flex-direction: column;
                            gap: 10px;
                        }

                        .admission-selected-year {
                            min-width: 100%;
                        }

                        .admission-download-action {
                            align-items: stretch;
                            flex-direction: column;
                        }

                        .admission-download-action .ant-btn {
                            width: 100%;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default AdmissionStuListByClass;